import React, { ChangeEvent, FC } from 'react';
import {
  Checkbox,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Unstable_Grid2 as Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { UseDateRange } from 'types/app';
import { Ordering, OrganicInsightsSortFields, SortDirections } from 'api';
import { DatePickers, PaddedPaper, LoaderComponents } from 'components';
import * as paths from 'paths';

import { RangePickerWrapper } from 'features/admin/components/shared';
import { CompaniesFilter, PeopleFilter } from '../types';
import { AdvancedFiltersModal } from './index';

interface Props extends Partial<UseDateRange> {
  filter: PeopleFilter | CompaniesFilter;
  setFilter: (filter: any) => void;
  total: number;
  queryLoading: boolean;
}

const FilterBar: FC<Props> = ({ filter, setFilter, total, queryLoading, ...rest }) => {
  const { startDate, endDate, dateRange, setDateRange, setStartDate, setEndDate } = rest;
  const { t } = useTranslation();
  const { pathname } = useLocation();

  let nextStartDate: string = null;
  let nextEndDate: string = null;

  function handleSetStartDate(date: string) {
    nextStartDate = date;
    setStartDate(nextStartDate);
  }

  function handleSetEndDate(date: string) {
    nextEndDate = date;
    setEndDate(nextEndDate);

    setFilter({
      ...filter,
      sort_by: JSON.stringify(filter.sort_by),
      users: filter?.users?.join(',') || '',
      posts: filter?.posts?.join(',') || '',
      company_pages: filter?.company_pages?.join(',') || '',
      start: nextStartDate,
      end: nextEndDate,
    });
  }

  function onChangeOrder(event: ChangeEvent<HTMLInputElement>) {
    const expected_sort =
      event.target.value === Ordering.Top
        ? { field: OrganicInsightsSortFields.Engagements, direction: SortDirections.Desc }
        : { field: OrganicInsightsSortFields.EngagementDate, direction: SortDirections.Desc };
    setFilter({
      ...filter,
      ordering: event.target.value,
      sort_by: JSON.stringify(expected_sort),
      users: filter?.users?.join(',') || '',
      posts: filter?.posts?.join(',') || '',
      company_pages: filter?.company_pages?.join(',') || '',
    });
  }

  function onChangeTeammates(event: ChangeEvent<HTMLInputElement>) {
    setFilter({
      ...filter,
      sort_by: JSON.stringify(filter.sort_by),
      users: filter?.users?.join(',') || '',
      posts: filter?.posts?.join(',') || '',
      company_pages: filter?.company_pages?.join(',') || '',
      hide_teammates: event.target.checked,
    });
  }

  function onChangeHideMyCompany(event: ChangeEvent<HTMLInputElement>) {
    setFilter({
      ...filter,
      sort_by: JSON.stringify(filter.sort_by),
      users: filter?.users?.join(',') || '',
      posts: filter?.posts?.join(',') || '',
      company_pages: filter?.company_pages?.join(',') || '',
      hide_company: event.target.checked,
    });
  }

  function renderCompaniesFilter() {
    return (
      <Grid>
        <FormControlLabel
          control={
            <Checkbox
              checked={(filter as CompaniesFilter).hide_company}
              onChange={onChangeHideMyCompany}
            />
          }
          label={t('insights:hide_my_company')}
        />
      </Grid>
    );
  }

  function renderPeopleFilter() {
    return (
      <Grid xs container justifyContent="flex-start" alignItems="center">
        <Grid>
          <Typography display="inline-block" variant="subtitle2">
            {t('common:order')}:{' '}
          </Typography>
        </Grid>

        <Grid>
          <RadioGroup row value={(filter as PeopleFilter).ordering} onChange={onChangeOrder}>
            <FormControlLabel
              value={Ordering.Top}
              control={<Radio color="primary" />}
              label={t('common:top')}
            />
            <FormControlLabel
              value={Ordering.Newest}
              control={<Radio color="primary" />}
              label={t('common:newest')}
            />
          </RadioGroup>
        </Grid>

        <Grid marginRight={1} height="auto" alignSelf="stretch">
          <Divider orientation="vertical" />
        </Grid>

        <Grid>
          <FormControlLabel
            control={
              <Checkbox
                checked={(filter as PeopleFilter).hide_teammates}
                onChange={onChangeTeammates}
              />
            }
            label={t('insights:hide_teammates')}
          />
        </Grid>

        <Grid>
          {queryLoading ? (
            <LoaderComponents.Block />
          ) : (
            <Typography variant="h6" color="primary">
              {t('insights:total_result', { count: total })}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  }

  function renderFilters() {
    switch (pathname) {
      case paths.insightsPeople:
        return renderPeopleFilter();
      case paths.insights:
      case paths.insightsCompanies:
        return renderCompaniesFilter();
      default:
        return null;
    }
  }

  function renderTotalResults(total: number) {
    if (pathname === paths.insightsPeople) {
      return null;
    }

    return (
      <Grid container xs>
        <Grid marginRight={1} height="auto" alignSelf="stretch">
          <Divider orientation="vertical" />
        </Grid>
        <Grid>
          {queryLoading ? (
            <LoaderComponents.Block />
          ) : (
            <Typography variant="h6" color="primary">
              {t('insights:total_result', { count: total })}
            </Typography>
          )}
        </Grid>
      </Grid>
    );
  }

  return (
    <PaddedPaper>
      <Grid container justifyContent="flex-start" alignItems="center" spacing={3}>
        <Grid>
          <RangePickerWrapper>
            <DatePickers.Range
              dateRange={dateRange}
              startDate={startDate}
              endDate={endDate}
              onSetStart={handleSetStartDate}
              onSetEnd={handleSetEndDate}
              onSetRange={setDateRange}
              includeToday
            />
          </RangePickerWrapper>
        </Grid>
        <Grid>
          <AdvancedFiltersModal filter={filter} setFilter={setFilter} />
        </Grid>

        {renderFilters()}
        {renderTotalResults(total)}
      </Grid>
    </PaddedPaper>
  );
};

export default FilterBar;
